import { TaskWarningModal } from '@otp/ui';
import { DocumentNode } from 'graphql';
import React, { cloneElement, useState } from 'react';
import { Command, useRunCommand } from 'src/hooks/query/use-run-command';
import { getValuesFromTask, isWarningRequired } from './task-helpers';
import { TaskPayload } from '../list/history/columns/types';
import { Play } from '@otp/icons';
import { ScheduledJobForm } from '../list/scheduled-jobs/form/scheduled-job';
import { Can } from 'src/utils/can';
import { Actions, AppArea, Subjects } from 'src/graphql/generated/graphql';

type RunCommandProps = {
  command: Command;
  refetchQueries?: DocumentNode[];
  children?: React.ReactElement;
  onRun?: () => void;
  task?: TaskPayload;
};

export const RunCommand = ({
  command,
  refetchQueries,
  children,
  onRun,
  task,
}: RunCommandProps) => {
  const { runCommand, loading } = useRunCommand();
  const [showTaskWarning, setShowTaskWarning] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  const handleClick = () => {
    if (
      !showTaskWarning &&
      isWarningRequired(command?.name, command?.triageId || command?.queryId)
    ) {
      setShowTaskWarning(true);
    } else {
      runCommand(command, { refetchQueries });
      onRun?.();
    }
  };

  const renderChildren = (children: React.ReactElement) =>
    cloneElement(children, { onClick: handleClick });

  let renderedChildren = <Play onClick={handleClick} />;

  if (children) {
    renderedChildren = renderChildren(children);
  }

  return (
    <Can I={Actions.Write} a={Subjects.Jobs} appArea={AppArea.Most}>
      {showTaskWarning && (
        <TaskWarningModal
          open={true}
          onClose={() => setShowTaskWarning(false)}
          onConfirm={handleClick}
          onSchedule={() => {
            setShowTaskWarning(false);
            setShowScheduleForm(true);
          }}
          onCancel={() => setShowTaskWarning(false)}
        />
      )}
      {showScheduleForm && (
        <ScheduledJobForm
          onClose={() => setShowScheduleForm(false)}
          onFinish={() => setShowScheduleForm(false)}
          loading={loading}
          defaultValues={getValuesFromTask(task as TaskPayload)}
          refetchQueries={refetchQueries}
        />
      )}
      {renderedChildren}
    </Can>
  );
};
